import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getCashRegisters, getCashShifts, getOrders } from '../../services/api';
import { formatCurrency } from '../../utils/formatCurrency';
import './DockWidgets.css';

const QuickCashWidget = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    loadCashData();
    const interval = setInterval(loadCashData, 60000); // Refresh every 60s
    const onShiftChanged = () => loadCashData();
    window.addEventListener('cashShiftChanged', onShiftChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener('cashShiftChanged', onShiftChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Calculate real-time sales for an open shift
  const calculateShiftSales = async (shiftId) => {
    try {
      let orders = [];
      try {
        const ordersData = await getOrders();
        orders = ordersData.orders || [];
      } catch (ordErr) {
        if (ordErr.response?.status === 404) {
          return { totalSales: 0, cashSales: 0 };
        }
        throw ordErr;
      }

      // Filter orders that belong to this shift and are delivered
      const shiftOrders = orders.filter(
        (order) => order.shiftId === shiftId && order.status === 'delivered'
      );

      let totalSales = 0;
      let cashSales = 0;

      shiftOrders.forEach((order) => {
        const orderTotal = order.total || 0;
        totalSales += orderTotal;

        // Count as cash sale if payment method is cash
        if (order.paymentMethod === 'cash') {
          cashSales += orderTotal;
        }
      });

      console.log(
        `[QuickCashWidget] Calculated sales for shift ${shiftId}: ${shiftOrders.length} orders, total: ${totalSales}, cash: ${cashSales}`
      );

      return { totalSales, cashSales };
    } catch (error) {
      console.error('[QuickCashWidget] Error calculating sales:', error);
      return { totalSales: 0, cashSales: 0 };
    }
  };

  const loadCashData = async () => {
    try {
      setError(false);
      let registers = [];
      try {
        const data = await getCashRegisters();
        registers = data.registers || [];
      } catch (regErr) {
        // Treat 404 as no registers (new restaurant without cash setup)
        if (regErr.response?.status === 404) {
          registers = [];
        } else {
          throw regErr;
        }
      }

      // Find open shift across all registers
      let foundShift = null;
      for (const register of registers) {
        // Get shifts for this register
        let shifts = [];
        try {
          const shiftsResponse = await getCashShifts(register.registerId);
          shifts = shiftsResponse.shifts || [];
        } catch (shiftErr) {
          // Treat 404 as no shifts for this register
          if (shiftErr.response?.status === 404) {
            continue;
          }
          throw shiftErr;
        }

        // Find open shift
        const openShift = shifts.find((s) => s.status === 'open');
        if (openShift) {
          // Calculate real-time sales for open shift
          const { totalSales, cashSales } = await calculateShiftSales(openShift.shiftId);

          foundShift = {
            ...openShift,
            registerName: register.name,
            totalSales,
            cashSales,
          };
          break;
        }
      }

      setShift(foundShift);
    } catch (err) {
      console.error('[QuickCashWidget] Error loading cash data:', err);
      setError(true);
      setShift(null);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="widget-loading">
        <span className="material-icons spinning">sync</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="widget-error">
        <span className="material-icons">cloud_off</span>
        <span>{t('dock.loadError', 'Error al cargar datos')}</span>
        <button type="button" className="widget-action-sm" onClick={loadCashData}>
          {t('common.retry', 'Reintentar')}
        </button>
      </div>
    );
  }

  return (
    <div className="quick-cash-widget">
      {!shift ? (
        <div className="widget-empty">
          <span className="material-icons">money_off</span>
          <span>{t('dock.noOpenShift', 'Sin turno abierto')}</span>
          <button
            type="button"
            className="widget-action-sm"
            onClick={() => navigate('/cash-register')}
          >
            {t('dock.openShift', 'Abrir turno')}
          </button>
        </div>
      ) : (
        <>
          <div className="cash-status">
            <span className="cash-status-badge open">
              <span className="material-icons">check_circle</span>
              {t('dock.shiftOpen', 'Turno abierto')}
            </span>
            <span className="cash-time">{formatTime(shift.openedAt)}</span>
          </div>

          <div className="cash-stats">
            <div className="cash-stat">
              <span className="stat-label">{t('dock.sales', 'Ventas')}</span>
              <span className="stat-value">{formatCurrency(shift.totalSales || 0)}</span>
            </div>
            <div className="cash-stat">
              <span className="stat-label">{t('dock.cash', 'Efectivo')}</span>
              <span className="stat-value">{formatCurrency(shift.cashSales || 0)}</span>
            </div>
          </div>

          <button
            type="button"
            className="widget-action"
            onClick={() => navigate('/cash-register')}
          >
            {t('dock.goToCash', 'Ir a Caja')}
            <span className="material-icons">arrow_forward</span>
          </button>
        </>
      )}
    </div>
  );
};

export default QuickCashWidget;
